import { useNavigate } from 'react-router-dom'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import type { ApplicationError, Return } from 'modules/core/lib/asyncAction'
import type { Adapter } from 'modules/core/lib/mobx-rest/types'

import { useContractsImporter } from 'modules/employees/components/ContractImporter/contractsImporterContext'

interface Props {
  api: Adapter
  file: File
  importPath: string
}
interface ReturnType {
  running: boolean
  onSubmit: (closePortal: ClosePortalFn) => Promise<Return<void>>
  error: boolean
  value: void | ApplicationError | null
}

const useProcessContractFile = ({ api, file, importPath }: Props): ReturnType => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const importer = useContractsImporter()

  const {
    running,
    performAction: onSubmit,
    error,
    value,
  } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!file) return

      const payload = await api.post(importPath, { file })
      const { token, contracts, attributes, custom_fields: customFields } = payload
      importer.setContracts({
        contracts,
        attributes,
        customFields,
        isOverlimited: payload.is_overlimited,
        newContracts: payload.new_contracts,
        currentSeats: payload.current_seats,
        maxContractsAllowedImport: payload.max_contracts_allowed_import,
      })

      importer.setToken(token)
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })
      navigate(navigation.employees.contractImport.fullPath())
      importer.setFiles([])
    },
    {
      errorFlash: false,
    }
  )

  return {
    running,
    onSubmit,
    error,
    value,
  }
}

export default useProcessContractFile
